import { Button, Col, Form, Modal, Row, TimePicker } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormItem from '@vindr/ui/src/components/common/FormItem/FormItem';
import useUserInfo from '@vindr/utils/hooks/useUserInfo';
import { generateUserLabel } from '@vindr/ui/src/components/common/FormItem/components/Technician';
import { useDebounce } from '@vindr/utils/hooks/useDebounce';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import OpenNewTab from '@vindr/ui/src/components/ui/OpenNewTab/OpenNewTab';
import { QMS_STATUS, QMS_Type } from '@vindr/services/apis/qms.api';
import { useGetQMSProfile, useGetQMS, useUpdateQMSProfile } from '@vindr/services/actions/qms.action';
import { useGlobalState } from '../../../contexts/GlobalContext';
import { uniquePushArray } from '@vindr/utils/helpers/common.helper';
import { parseTime, formatTime } from '@vindr/utils/dateTime';

const { RangePicker } = TimePicker;

const QmsProfile = ({
  isLoading = false,
  // onSubmit,
  okText = 'Save',
  closable = true,
  cancelText = 'Cancel',
  onCancel = () => {},
}) => {
  const { message } = useGlobalState();
  const { t } = useTranslation('Vindoc');
  const { data: userInfo } = useUserInfo();
  const [form] = Form.useForm();
  const { data: qmsProfile } = useGetQMSProfile()?.data || {};
  const { mutate: updateQMSProfile } = useUpdateQMSProfile();

  useEffect(() => {
    form.setFieldsValue({
      institution_id: qmsProfile?.institution_id || userInfo?.default_institution_id,
      qms_order_default_id: qmsProfile?.qms_order_default_id,
      qms_order_worklist_id: qmsProfile?.qms_order_worklist_id,
      order_morning_time: [
        parseTime(qmsProfile?.qms_order_default?.morning_shift_start),
        parseTime(qmsProfile?.qms_order_default?.morning_shift_end),
      ],
      order_afternoon_time: [
        parseTime(qmsProfile?.qms_order_default?.afternoon_shift_start),
        parseTime(qmsProfile?.qms_order_default?.afternoon_shift_end),
      ],
      order_doctor_id: qmsProfile?.qms_order_default?.doctor_id,
      order_status: qmsProfile?.qms_order_default?.status,
      worklist_morning_time: [
        parseTime(qmsProfile?.qms_worklist_default?.morning_shift_start),
        parseTime(qmsProfile?.qms_worklist_default?.morning_shift_end),
      ],
      worklist_afternoon_time: [
        parseTime(qmsProfile?.qms_worklist_default?.afternoon_shift_start),
        parseTime(qmsProfile?.qms_worklist_default?.afternoon_shift_end),
      ],
      worklist_doctor_id: qmsProfile?.qms_worklist_default?.doctor_id,
      worklist_status: qmsProfile?.qms_worklist_default?.status,
    });
  }, [form, qmsProfile]);

  const [searchInstitution, onSearchInstitution] = useDebounce<string>();
  const { data: institutions } =
    useGetInstitutions({ offset: 0, limit: 100, name: searchInstitution })?.data || {};

  const institution_id = Form.useWatch('institution_id', form);
  const selectedInstitution = useMemo(() => institutions?.find((item) => item.id === institution_id), [institutions, institution_id]);
  const qms_order_default_id = Form.useWatch('qms_order_default_id', form);
  const qms_order_worklist_id = Form.useWatch('qms_order_worklist_id', form);

  const { data: qmsList } = useGetQMS({
    limit: 100,
    offset: 0,
    status: QMS_STATUS.Active,
    institution_ids: [institution_id],
  })?.data || {};

  const qmsToSelect = qmsList || [];

  const [searchDevice, onSearchDevice] = useDebounce<string>();

  useEffect(() => {
    if (institution_id && institution_id !== qmsProfile?.institution_id) {
      form.setFieldValue('qms_order_default_id', null);
      form.setFieldValue('qms_order_worklist_id', null);
    }
  }, [institution_id]);

  useEffect(() => {
    const selectedOrderQms = qmsToSelect?.find((item) => item.id === qms_order_default_id);
    if (!selectedOrderQms) return;
    form.setFieldValue(
      'order_morning_time',
      [
        parseTime(selectedOrderQms?.morning_shift_start),
        parseTime(selectedOrderQms?.morning_shift_end),
      ]
    );
    form.setFieldValue(
      'order_afternoon_time',
      [
        parseTime(selectedOrderQms?.afternoon_shift_start),
        parseTime(selectedOrderQms?.afternoon_shift_end),
      ]
    );
    form.setFieldValue('order_doctor_id', selectedOrderQms?.doctor_id);
    form.setFieldValue('order_status', selectedOrderQms?.status);
  }, [qms_order_default_id]);

  useEffect(() => {
    const selectedWorklistQms = qmsToSelect?.find((item) => item.id === qms_order_worklist_id);
    if (!selectedWorklistQms) return;
    form.setFieldValue(
      'worklist_morning_time',
      [
        parseTime(selectedWorklistQms?.morning_shift_start),
        parseTime(selectedWorklistQms?.morning_shift_end),
      ]
    );
    form.setFieldValue(
      'worklist_afternoon_time',
      [
        parseTime(selectedWorklistQms?.afternoon_shift_start),
        parseTime(selectedWorklistQms?.afternoon_shift_end),
      ]
    );
    form.setFieldValue('worklist_doctor_id', selectedWorklistQms?.doctor_id);
    form.setFieldValue('worklist_status', selectedWorklistQms?.status);
  }, [qms_order_worklist_id]);

  const actionSave = () => {
    form
      .validateFields()
      .then((values) => {
        const qms = [];
        console.log('values', values);
        if (values.qms_order_default_id) {
          qms.push({
            id: values.qms_order_default_id,
            doctor_id: values?.order_doctor_id || null,
            status: values.order_status,
            morning_shift_start: formatTime(values.order_morning_time?.[0]),
            morning_shift_end: formatTime(values.order_morning_time?.[1]),
            afternoon_shift_start: formatTime(values.order_afternoon_time?.[0]),
            afternoon_shift_end: formatTime(values.order_afternoon_time?.[1]),
          });
        }
        if (values.qms_order_worklist_id) {
          qms.push({
            id: values.qms_order_worklist_id,
            doctor_id: values?.worklist_doctor_id || null,
            status: values.worklist_status,
            morning_shift_start: formatTime(values.worklist_morning_time?.[0]),
            morning_shift_end: formatTime(values.worklist_morning_time?.[1]),
            afternoon_shift_start: formatTime(values.worklist_afternoon_time?.[0]),
            afternoon_shift_end: formatTime(values.worklist_afternoon_time?.[1]),
          });
        }
        const params = {
          qms,
          institution_id: values.institution_id,
          qms_order_default_id: values.qms_order_default_id,
          qms_order_worklist_id: values.qms_order_worklist_id,
        };
        updateQMSProfile(params, {
          onSuccess: () => {
            message.success(t(MESSAGE.SUCCESS.SCD_34007));
            onCancel();
          },
          onError: (err) => {
            console.log(err);
            message.error(t(MESSAGE.ERROR[err?.response?.data?.error_code || MESSAGE.ERROR.EMSG_0001]));
          },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const selectedOrderQms = qmsToSelect?.find((item) => item.id === qms_order_default_id);
  const selectedWorklistQms = qmsToSelect?.find((item) => item.id === qms_order_worklist_id);

  return (
    <Modal
      centered
      forceRender
      closable={closable}
      confirmLoading={isLoading}
      footer={null}
      maskClosable={false}
      open={true}
      title={t('QMS Profile')}
      width={800}
      onCancel={onCancel}
    >
      <Form form={form} layout="vertical" name="edit-account" onFinish={actionSave}>
        <Row gutter={32}>
          <Col xl={24} xs={24}>
            <FormItem.InstitutionList
              required
              showSearch
              allowClear={false}
              multiple={false}
              label={t('Facility')}
              name="institution_id"
              placeholder={t('Select Facility')}
              wrapperClassName="w-full"
              onSearch={onSearchInstitution}
            />

            <div className="flex items-stretch space-x-3">
              <FormItem.Select
                // disabled
                required={false}
                className="w-full"
                label={t('Default Order QMS')}
                name="qms_order_default_id"
                options={qmsToSelect?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                placeholder={t('Select')}
                wrapperClassName="flex-1"
              />
              <OpenNewTab
                className="mt-[34px]"
                href={`/qms-portal/${selectedOrderQms?.type === QMS_Type.DEVICE ? 'device' : 'overview'}?id=${qms_order_default_id}`}
                rel="noopener noreferrer"
                title={t('Open in New Tab')}
                titleClassName="hidden min-[370px]:block"
                disabled={!qms_order_default_id}
                onClick={(e) => {
                  if (!qms_order_default_id) {
                    e.preventDefault();
                    message.error(t('Please select default order QMS'));
                  }
                }}
              />
            </div>
            {selectedOrderQms?.type === QMS_Type.DEVICE && <QMSDetail selectedInstitution={selectedInstitution} userInfo={userInfo} prefix="order_" />}
            <div className="flex items-stretch space-x-3">
              <FormItem.Select
                required={false}
                searchVietnamese
                showSearch
                className="mb-0 w-full"
                label={t('Default Worklist QMS')}
                name="qms_order_worklist_id"
                options={qmsToSelect?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                placeholder={t('Select')}
                wrapperClassName="flex-1"
                onSearch={onSearchDevice}
                onClear={() => {
                  onSearchDevice(null);
                }}
              />
              <OpenNewTab
                className="mt-[34px]"
                href={`/qms-portal/${selectedWorklistQms?.type === QMS_Type.DEVICE ? 'device' : 'overview'}?id=${qms_order_worklist_id}`}
                rel="noopener noreferrer"
                title={t('Open in New Tab')}
                disabled={!qms_order_worklist_id}
                onClick={(e) => {
                  if (!qms_order_worklist_id) {
                    e.preventDefault();
                    message.error(t('Please select default worklist QMS'));
                  }
                }}
              />
            </div>
            {selectedWorklistQms?.type === QMS_Type.DEVICE && <QMSDetail selectedInstitution={selectedInstitution} userInfo={userInfo} prefix="worklist_" />}
          </Col>
        </Row>
      </Form>
      <div className="footer mt-3 flex justify-end">
        {closable && (
          <Button className="mr-2" onClick={onCancel}>
            {cancelText}
          </Button>
        )}
        <Button type="primary" onClick={actionSave}>
          {okText}
        </Button>
      </div>
    </Modal>
  );
};

export default QmsProfile;

export const QMSDetail = ({ selectedInstitution, userInfo, prefix = '' }: { selectedInstitution?: any; userInfo: ReturnType<typeof useUserInfo>['data']; prefix?: string }) => {
  const { t } = useTranslation('Vindoc');

  return (
    <div>
      <div className="work-time mb-6">
        <div className="label mb-2">{t('Working Time')}</div>
        <div className="pl-5>">
          <div className="mb-2 flex items-center">
            <div className="ml-5 mr-2 w-1/3">{t('Morning')}</div>
            <Form.Item noStyle required className="mb-2" label={t('Morning')} name={`${prefix}morning_time`}>
              <RangePicker
                format="HH:mm"
                placeholder={['HH:mm', 'HH:mm']}
                className="w-full"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Form.Item>
          </div>
          <div className="flex items-center">
            <div className="ml-5 mr-2 w-1/3">{t('Afternoon')}</div>
            <Form.Item
              noStyle
              required
              className="mb-2"
              label={t('Afternoon')}
              name={`${prefix}afternoon_time`}
            >
              <RangePicker
                format="HH:mm"
                placeholder={['HH:mm', 'HH:mm']}
                className="w-full"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Form.Item>
          </div>
        </div>
      </div>

      <FormItem.Technician
        placeholder={t('Select')}
        {...(userInfo?.id && {
          selected: {
            value: userInfo?.id,
            label: generateUserLabel(userInfo),
          },
        })}
        institutions={selectedInstitution ? [selectedInstitution.code] : []}
        label={'Doctor'}
        name={`${prefix}doctor_id`}
        required={false}
        size="middle"
      />

      <FormItem.Select
        required={true}
        label={t('Status')}
        name={`${prefix}status`}
        placeholder={t('Select')}
        options={[
          { label: t('Active'), value: 1 },
          { label: t('Inactive'), value: 0 },
        ]}
      />
    </div>
  );
};
